"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/context/AuthContext";
import { usePortal } from "@/context/PortalContext";
import { db } from "@/firebase/config";
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  withLabel,
} from "@/lib/firestore-instrumented";
import {
  filterInboxItemsForPortal,
  findFreshUnreadItems,
  INBOX_NOTIFICATION_LIMIT,
  sortInboxItems,
} from "@/lib/inboxNotifications.mjs";

const InboxNotificationsContext = createContext(null);

export function InboxNotificationsProvider({ children }) {
  const { user } = useAuth();
  const portal = usePortal();
  const [rawItems, setRawItems] = useState([]);
  const [rawFreshItems, setRawFreshItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(user?.uid));
  const [loadedUid, setLoadedUid] = useState(null);
  const [error, setError] = useState("");
  const previousItemsRef = useRef([]);
  const initializedUidRef = useRef(null);

  useEffect(() => {
    const uid = user?.uid || null;
    if (!uid) {
      previousItemsRef.current = [];
      initializedUidRef.current = null;
      setRawItems([]);
      setRawFreshItems([]);
      setLoading(false);
      setLoadedUid(null);
      setError("");
      return undefined;
    }

    previousItemsRef.current = [];
    initializedUidRef.current = null;
    setRawItems([]);
    setRawFreshItems([]);
    setLoading(true);
    setLoadedUid(null);
    setError("");

    const events = collection(db, `userInbox/${uid}/events`);
    const inboxQuery = withLabel(
      query(
        events,
        orderBy("createdAt", "desc"),
        limit(INBOX_NOTIFICATION_LIMIT)
      ),
      `Inbox:events(limit=${INBOX_NOTIFICATION_LIMIT})`
    );

    return onSnapshot(
      inboxQuery,
      (snapshot) => {
        const nextItems = snapshot.docs.map((item) => ({
          id: item.id,
          ...(item.data() || {}),
        }));
        const initialized = initializedUidRef.current === uid;
        setRawFreshItems(
          findFreshUnreadItems(previousItemsRef.current, nextItems, initialized)
        );
        previousItemsRef.current = nextItems;
        initializedUidRef.current = uid;
        setRawItems(nextItems);
        setLoading(false);
        setLoadedUid(uid);
        setError("");
      },
      (snapshotError) => {
        console.warn("[Inbox] notification listen error", snapshotError);
        setRawItems([]);
        setRawFreshItems([]);
        setLoading(false);
        setLoadedUid(uid);
        setError("Could not load notifications.");
      }
    );
  }, [user?.uid]);

  const items = useMemo(
    () => filterInboxItemsForPortal(rawItems, portal?.allowedHubSlugSet),
    [portal?.allowedHubSlugSet, rawItems]
  );
  const freshItems = useMemo(
    () => filterInboxItemsForPortal(rawFreshItems, portal?.allowedHubSlugSet),
    [portal?.allowedHubSlugSet, rawFreshItems]
  );
  const unreadCount = useMemo(
    () => items.filter((item) => !item.read).length,
    [items]
  );
  const sessionLoading = Boolean(
    user?.uid && (loading || loadedUid !== user.uid)
  );

  useEffect(() => {
    const nav = typeof navigator === "undefined" ? null : navigator;
    if (
      nav &&
      typeof nav.setAppBadge === "function" &&
      typeof nav.clearAppBadge === "function"
    ) {
      if (unreadCount > 0) nav.setAppBadge(unreadCount).catch(() => {});
      else nav.clearAppBadge().catch(() => {});
    }

    nav?.serviceWorker?.ready
      ?.then((registration) => registration?.active?.postMessage?.({
        type: "BADGE",
        count: unreadCount,
      }))
      ?.catch(() => {});
  }, [unreadCount]);

  const markRead = useCallback(async (notification) => {
    const uid = user?.uid;
    if (!uid || notification?.read || !notification?.id) return;
    setRawItems((current) => current.map((item) => (
      item.id === notification.id ? { ...item, read: true } : item
    )));

    try {
      await updateDoc(
        doc(db, `userInbox/${uid}/events/${notification.id}`),
        { read: true }
      );
    } catch (updateError) {
      setRawItems((current) => current.map((item) => (
        item.id === notification.id ? { ...item, read: false } : item
      )));
      throw updateError;
    }
  }, [user?.uid]);

  const remove = useCallback(async (notification) => {
    const uid = user?.uid;
    if (!uid || !notification?.id) return;
    const reference = doc(db, `userInbox/${uid}/events/${notification.id}`);
    setRawItems((current) => current.filter((item) => item.id !== notification.id));
    try {
      await deleteDoc(reference);
    } catch (deleteError) {
      setRawItems((current) => {
        if (current.some((item) => item.id === notification.id)) return current;
        return sortInboxItems([...current, notification]);
      });
      throw deleteError;
    }
  }, [user?.uid]);

  const value = useMemo(() => ({
    error,
    freshItems,
    items,
    limit: INBOX_NOTIFICATION_LIMIT,
    loading: sessionLoading,
    markRead,
    remove,
    unreadCount,
    user,
  }), [
    error,
    freshItems,
    items,
    markRead,
    remove,
    sessionLoading,
    unreadCount,
    user,
  ]);

  return (
    <InboxNotificationsContext.Provider value={value}>
      {children}
    </InboxNotificationsContext.Provider>
  );
}

export function useInboxNotificationsContext() {
  const value = useContext(InboxNotificationsContext);
  if (!value) {
    throw new Error("useInboxNotifications must be used within InboxNotificationsProvider.");
  }
  return value;
}
